import {InvoiceListResponse, InvoiceListResponseItem} from "@/api/InvoicesAPI";
import React, {useEffect, useState, useRef} from "react";
import {toast} from "@/components/ui/use-toast";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import DatePicker from "react-datepicker";

const handleDownloadInvoicePDF = async (invoiceId: number) => {
    try {
        const response = await ApiHttpClient().get(`/invoice/pdf/${invoiceId}?businessType=Franchise`);

        if (response.status === 200) {
            const {pdfBytes, fileName} = response.data;
            // Decode the base64 string
            const byteCharacters = atob(pdfBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            
            // Create a Blob from the byteArray
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', fileName || 'Factura.pdf');
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea facturii, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea facturii', error);
    }
}

const handleDownloadInvoiceXML = async (invoiceId: number, invoiceNumber: string) => {
    try {
        const response = await ApiHttpClient().get(`/invoice/xml/${invoiceId}?businessType=Franchise`);

        if (response.status === 200) {
            const xml = response.data as string;

            // Create a Blob from the xml
            const blob = new Blob([xml], {type: ' text/xml', endings: 'native'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const filename = "Factura_" + invoiceNumber.replaceAll(' ', '_') + '.xml';
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        } else {
            console.error('A aparut o eroare la descarcarea facturii, mesajul:', response.status);
        }
    } catch (error) {
        console.error('A aparut o eroare la descarcarea facturii', error);
    }
}

const SearchInput: React.FC<any> = ({searchInvoice, setSearchInvoice, setIsNewSearch}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length > 2) {
            setSearchInvoice(e.target.value);
            setIsNewSearch(true);
        } else {
            setSearchInvoice();
        }
    };

    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            value={searchInvoice}
            onChange={handleChange}
            placeholder="Cauta codul unei facturi, numele organizatiei sau punctul de lucru..."
        />
    );
};

export default function InvoiceListPage() {
    const [currentPage, setCurrentPage] = useState(0);
    const [searchInvoice, setSearchInvoice] = useState("");
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [data, setData] = useState<InvoiceListResponse>();
    const [dateMin, setDateMin] = useState<any>();
    const [dateMax, setDateMax] = useState<any>(new Date());
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const pageSize = 30;

    const detailsRef = useRef<HTMLDetailsElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.removeAttribute("open");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
        setIsNewSearch(false);
    };

    const handleRemoveFilters = () => {
        setSearchInvoice('');
        setIsNewSearch(false);
        setDateMin('');
        setDateMax(new Date());
    }

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (data?.totalPages && value > data.totalPages) {
            value = data.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (data?.totalPages && value >= 1 && value <= data?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };


    useEffect(() => {
        const updateInvoices = async () => {
            setIsLoadingSearch(true);

            let response;
            const filters = [];

            if (dateMin) {
                filters.push({
                    field: "invoiceDate",
                    operator: "gte",
                    value: new Date(dateMin).toISOString()
                });
            }

            if(dateMax) {
                filters.push({
                    field: "invoiceDate",
                    operator: "lte",
                    value: new Date(dateMax).toISOString()
                });
            }

            const requestBody = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                pageNumber: currentPage + 1,
                pageSize: pageSize
            }

            const requestBodySearchAndDate = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                advancedSearch: {
                    fields: [
                        "invoiceNumber",
                        "workingPoint.Organization.Name",
                        "workingPoint.Id"
                    ],
                    keyword: searchInvoice
                },
                pageNumber: currentPage + 1,
                pageSize: pageSize,
            }
            
            if (searchInvoice) {
                if (isNewSearch) setCurrentPage(0);

                if (dateMin && dateMax) {
                    response = await  ApiHttpClient().post<InvoiceListResponse>('/invoice/search', requestBodySearchAndDate);
                } else {
                    response = await ApiHttpClient().post<InvoiceListResponse>('/invoice/search', {
                        "advancedSearch": {
                            "fields": [
                                "invoiceNumber",
                                "workingPoint.Organization.Name",
                                "workingPoint.Id"
                            ],
                            "keyword": searchInvoice
                        },
                        "pageNumber": currentPage + 1,
                        "pageSize": pageSize,
                    });
                }
            } else if (dateMin && dateMax) {
                response = await  ApiHttpClient().post<InvoiceListResponse>(`/invoice/search`, requestBody);
            } else {
                response = await ApiHttpClient().post<InvoiceListResponse>(`/invoice/search`, {
                    "keyword": "",
                    "pageNumber": currentPage + 1,
                    "pageSize": pageSize,
                    "orderBy": ["invoiceNumber Desc"]
                });
            }
            if (!response?.data) {
                toast({description: "A aparut o eroare pentru afisarea precomenzilor"});
                return <></>;
            }
            setIsLoadingSearch(false);
            setData(response.data);
        }

        updateInvoices();
    }, [searchInvoice, currentPage, pageSize, isNewSearch, dateMin, dateMax])

    return (
        <>
            <Navbar/>
            <div className="flex flex-row items-start">
                <SideMenu/>
                {/* Desktop Version */}
                <div className="hidden mx-auto md:block z-auto">
                    {!isLoadingSearch && data?.totalPages === 0 && <p className="mt-5 ml-5 mb-3">Nu au fost gasite rezultate.</p>}
                    <div className=" relative overflow-x-auto mx-8 shadow-md sm:rounded-lg overflow-y-auto pt-4 pb-16">

                        <div className="flex justify-end mb-2 mr-2">
                            <button
                                className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                                onClick={handleRemoveFilters}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    className="size-3"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                </svg>

                                <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                            </button>
                        </div>

                        <div className="mb-5">
                            <SearchInput searchInvoice={searchInvoice} setSearchInvoice={setSearchInvoice}
                                         setIsNewSearch={setIsNewSearch}/>
                        </div>

                        <details ref={detailsRef} className="group relative mb-5">
                            <summary
                                className="mx-2 flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                            >
                                <span className="text-sm font-medium"> Cauta facturile din </span>

                                <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                    </svg>
                                  </span>
                            </summary>

                            <div
                                className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                            >
                                <div className="flex items-center justify-between px-3 py-2">
                                    <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDateMin('');
                                            setDateMax(new Date())
                                        }}
                                        className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                    >
                                        Reset
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 p-3">
                                    <label htmlFor="MinPrice">
                                        <span className="text-sm text-gray-700"> Data de inceput </span>

                                        <DatePicker
                                            selected={dateMin}
                                            onChange={(dateMin) => setDateMin(dateMin)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []}, // Prevents flipping above
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                        />
                                    </label>

                                    <label htmlFor="MaxPrice">
                                        <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                        <DatePicker
                                            selected={dateMax}
                                            onChange={(dateMax) => setDateMax(dateMax)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []},
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal"
                                        />
                                    </label>
                                </div>
                            </div>
                        </details>

                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead
                                className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-6 py-3">
                                    Id Factura
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Punct de lucru
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Numele organizatiei
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Data
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Livrari
                                </th>
                                <th scope="col" className="px-6 py-3 max-w-xl">
                                    Precomenzi
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Descarca Factura
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {data?.items.map((invoice: InvoiceListResponseItem) => {
                                return (
                                    <tr className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700">
                                        <td className="px-6 py-4">
                                            {invoice.invoiceSeries} {invoice.invoiceNumber}
                                        </td>
                                        <td className="px-6 py-4">
                                            {invoice.status}
                                        </td>
                                        <td className="px-6 py-4">
                                            {invoice.workingPointId}
                                        </td>
                                        <td className="px-6 py-4">
                                            {invoice.organizationName}
                                        </td>
                                        <td className="px-6 py-4">
                                            {invoice.date?.split("T")[0]}
                                        </td>
                                        <th scope="row"
                                            className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap  dark:text-white max-w-xl align-top">
                                            <div className="flex flex-wrap overflow-hidden justify-start">
                                                {invoice.shippings.map((shipping: number) =>
                                                    <a href={"/my-account/shipping/" + shipping}
                                                       className="text-blue-500 underline z-auto m-1 w-1/4 sm:w-1/2 md:w-1/3 lg:w-1/4 mx-2">{shipping}</a>
                                                )}
                                            </div>
                                        </th>
                                        <th scope="row"
                                            className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white max-w-xl align-top">
                                            <div className="flex flex-wrap overflow-hidden justify-start">
                                                {invoice.preOrders.map((preOrder: number) =>
                                                    <a href={"/my-account/preorders/pre-order-detail/" + preOrder}
                                                       className="text-blue-500 underline z-auto mx-1 sm:w-1/2 md:w-1/3 lg:w-1/4">{preOrder}</a>
                                                )}
                                            </div>
                                        </th>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col text-center">
                                                <button onClick={() => handleDownloadInvoicePDF(invoice.id)}
                                                        className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                    Download PDF
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadInvoiceXML(invoice.id, invoice.invoiceNumber)}
                                                    className="mb-1 font-medium text-red-600 hover:text-red-500">
                                                    Download XML
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            </tbody>
                        </table>
                    </div>

                    <div className="text-center mt-8 mb-2">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (data && currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={data && currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Mobile Version */}
                <div className="mx-auto max-w-screen-xl md:hidden z-0">
                    {!isLoadingSearch && data?.totalPages === 0 && <p>Nu au fost gasite rezultate.</p>}

                    <div className="flex justify-end mt-4 mr-2">
                        <button
                            className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                            onClick={handleRemoveFilters}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                className="size-3"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>

                            <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                        </button>
                    </div>

                    <div className="-my-3 py-3 ml-12 mt-2 mb-8 mr-12">
                        <SearchInput searchInvoice={searchInvoice} setSearchInvoice={setSearchInvoice}
                                     setIsNewSearch={setIsNewSearch}/>
                    </div>

                    <details className="group relative ml-12">
                        <summary
                            className="flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                        >
                            <span className="text-sm font-medium"> Cauta comenzile din </span>

                            <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                    </svg>
                                  </span>
                        </summary>

                        <div
                            className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                        >
                            <div className="flex items-center justify-between px-3 py-2">
                                <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setDateMin('');
                                        setDateMax(new Date())
                                    }}
                                    className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="flex items-center gap-3 p-3">
                                <label htmlFor="MinPrice">
                                    <span className="text-sm text-gray-700"> Data de inceput </span>

                                    <DatePicker
                                        selected={dateMin}
                                        onChange={(dateMin) => setDateMin(dateMin)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: {boundary: "viewport", tether: false},
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: {fallbackPlacements: []}, // Prevents flipping above
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                    />
                                </label>

                                <label htmlFor="MaxPrice">
                                    <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                    <DatePicker
                                        selected={dateMax}
                                        onChange={(dateMax) => setDateMax(dateMax)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: {boundary: "viewport", tether: false},
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: {fallbackPlacements: []},
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal"
                                    />
                                </label>
                            </div>
                        </div>
                    </details>

                    {data?.items.map((invoice: InvoiceListResponseItem) => {
                        return (
                            <div
                                className="flow-root rounded-lg border border-gray-100 py-3 ml-12 mt-8 mb-8 mr-12 shadow-sm">
                                <dl className="-my-3 divide-y divide-gray-100 text-sm">
                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Id Factura</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{invoice.invoiceSeries} {invoice.invoiceNumber}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Status</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{invoice.status}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Punct de lucru</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{invoice.workingPointId}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Numele oragnizatiei</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{invoice.organizationName}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{invoice.date?.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Livrari</dt>
                                        <dd className="text-gray-700 sm:col-span-2">
                                            <div className="flex flex-wrap">
                                                {invoice.shippings.map((shipping: number) =>
                                                    <a href={"/my-account/shipping/" + shipping}
                                                       className="text-blue-500 m-1 underline z-auto w-1/4">{shipping}</a>
                                                )}
                                            </div>
                                        </dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Precomenzi</dt>
                                        <dd className="text-gray-700 sm:col-span-2">
                                            {invoice.preOrders.map((preOrder: number) =>
                                                <a href={"/my-account/preorders/pre-order-detail/" + preOrder}
                                                   className="text-blue-500 m-1 underline z-auto">{preOrder}</a>
                                            )}
                                        </dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Descarca Factura</dt>
                                        <dd className="text-gray-700 sm:col-span-2 flex flex-col">
                                            <button onClick={() => handleDownloadInvoicePDF(invoice.id)}
                                                    className="mb-1 text-left font-medium text-red-600 hover:text-red-500">
                                                Download PDF
                                            </button>
                                            <button
                                                onClick={() => handleDownloadInvoiceXML(invoice.id, invoice.invoiceNumber)}
                                                className="mb-1 text-left font-medium text-red-600 hover:text-red-500">
                                                Download XML
                                            </button>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        );
                    })}

                    <div className="text-center mb-4">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (data && currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={data && currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
}